
import React, { useState } from "react";
import { MoreHorizontal, Download, Trash2, Languages, FileSearch } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { da } from "date-fns/locale";
import { formatBytes } from "@/utils/format";
import { isAnalysisSupported } from "@/utils/documentParser";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { TranslateButton } from "../translation/TranslateButton";
import { useLanguage } from "@/contexts/LanguageContext";
import { useTranslation } from "react-i18next";
import { ProcurementDocumentAnalysis } from "./ProcurementDocumentAnalysis";
import { DocumentTranslator } from "../translation/DocumentTranslator";
import { MultiDocumentAnalysis } from "./MultiDocumentAnalysis";
import { useToast } from "@/hooks/use-toast";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export interface Document {
  id: string;
  file_name: string;
  file_type: string;
  file_size: number;
  created_at: string;
  file_path: string;
  description?: string;
}

interface DocumentTableProps {
  documents: Document[];
  isLoading: boolean;
  tenderId: string;
  onDownload: (filePath: string, fileName: string) => void;
  onDelete: (id: string, filePath: string) => void;
}

export const DocumentTable = ({
  documents,
  isLoading,
  tenderId,
  onDownload,
  onDelete,
}: DocumentTableProps) => {
  const [selectedDocs, setSelectedDocs] = useState<string[]>([]);
  const { t } = useTranslation('offerManagement');
  const { currentLanguage } = useLanguage();
  const { toast } = useToast();
  
  const [isTranslateDialogOpen, setIsTranslateDialogOpen] = useState(false);
  const [isMultiAnalyzeDialogOpen, setIsMultiAnalyzeDialogOpen] = useState(false);
  const [currentDocId, setCurrentDocId] = useState<string | null>(null);
  const [currentDocPath, setCurrentDocPath] = useState<string | null>(null);
  const [currentFileName, setCurrentFileName] = useState<string | null>(null);
  
  // Check if a document is supported for analysis
  const isDocumentAnalysisSupported = (doc: Document) => isAnalysisSupported(doc.file_type);
  
  // Only allow selecting documents that can be analyzed
  const handleToggleSelect = (id: string) => {
    setSelectedDocs((prev) =>
      prev.includes(id) ? prev.filter((docId) => docId !== id) : [...prev, id]
    );
  };

  const handleToggleAll = () => {
    if (selectedDocs.length === documents.length) {
      setSelectedDocs([]);
    } else {
      setSelectedDocs(documents.map((doc) => doc.id));
    }
  };

  const handleTranslate = (docId: string) => {
    const doc = documents.find((d) => d.id === docId);
    if (doc) {
      setCurrentDocId(doc.id);
      setCurrentDocPath(doc.file_path);
      setCurrentFileName(doc.file_name);
      setIsTranslateDialogOpen(true);
    }
  };

  const getFormattedDate = (dateString: string) => {
    try {
      return formatDistanceToNow(new Date(dateString), { 
        addSuffix: true,
        locale: currentLanguage === 'da' ? da : undefined
      });
    } catch (e) {
      return dateString;
    }
  };

  const handleBulkDownload = () => {
    selectedDocs.forEach((selectedId) => {
      const doc = documents.find((d) => d.id === selectedId);
      if (doc) {
        onDownload(doc.file_path, doc.file_name);
      }
    });
  };

  const handleBulkDelete = () => {
    selectedDocs.forEach((selectedId) => {
      const doc = documents.find((d) => d.id === selectedId);
      if (doc) {
        onDelete(doc.id, doc.file_path);
      }
    });
    setSelectedDocs([]);
  };

  const handleBulkTranslate = () => {
    console.log("Bulk translate for documents:", selectedDocs);
    
    if (selectedDocs.length === 1) {
      handleTranslate(selectedDocs[0]);
    } else if (selectedDocs.length > 1) {
      const firstDoc = documents.find(doc => doc.id === selectedDocs[0]);
      if (firstDoc) {
        setCurrentDocId(firstDoc.id);
        setCurrentDocPath(firstDoc.file_path);
        setCurrentFileName(firstDoc.file_name);
        setIsTranslateDialogOpen(true);
      }
    }
  };

  const handleBulkAnalyze = () => {
    const selectedDocuments = documents.filter(doc => selectedDocs.includes(doc.id));
    console.log(`Analyzing ${selectedDocuments.length} documents`, selectedDocuments);
    
    // Check if any of the selected documents are not supported
    const unsupportedDocs = selectedDocuments.filter(doc => !isDocumentAnalysisSupported(doc));
    
    if (unsupportedDocs.length > 0) {
      toast({
        title: "Error",
        description: currentLanguage === 'da'
          ? "Nogle af de valgte dokumenter er ikke i et understøttet format. Konvertér venligst til PDF eller brug et understøttet billedformat."
          : "Some of the selected documents are not in a supported format. Please convert to PDF or use a supported image format.",
        variant: "destructive",
      });
      return;
    }
    
    if (selectedDocuments.length > 0) {
      setIsMultiAnalyzeDialogOpen(true);
    }
  };

  const selectedDocuments = documents.filter(doc => selectedDocs.includes(doc.id));

  return (
    <div className="space-y-4">
      {selectedDocs.length > 0 && (
        <div className="flex items-center gap-2 bg-muted p-2 rounded-md">
          <span className="text-sm font-medium">
            {t("documents.selected", { count: selectedDocs.length })}
          </span>
          <Button variant="ghost" size="sm" onClick={() => setSelectedDocs([])}>
            {t("documents.deselectAll")}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleBulkTranslate}
          >
            {t("documents.translate")}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleBulkDownload}
          >
            {t("documents.download")}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleBulkDelete}
          >
            {t("documents.delete")}
          </Button>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleBulkAnalyze}
                    disabled={selectedDocuments.some(doc => !isDocumentAnalysisSupported(doc))}
                  >
                    {t("documents.analyzeSelectedDocuments")}
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent side="top">
                {selectedDocuments.some(doc => !isDocumentAnalysisSupported(doc))
                  ? currentLanguage === 'da'
                    ? "Nogle af de valgte dokumenter er ikke i et understøttet format. Konvertér venligst til PDF eller brug et understøttet billedformat."
                    : "Some of the selected documents are not in a supported format. Please convert to PDF or use a supported image format."
                  : currentLanguage === 'da'
                    ? "Analysér de valgte dokumenter"
                    : "Analyze the selected documents"
                }
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      )}

      {isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
        </div>
      ) : documents.length === 0 ? (
        <Alert variant="default">
          <AlertDescription>{t("documents.noDocuments")}</AlertDescription>
        </Alert>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[30px]">
                  <Checkbox
                    checked={
                      selectedDocs.length > 0 &&
                      selectedDocs.length === documents.length
                    }
                    onCheckedChange={handleToggleAll}
                    aria-label={t("documents.selectAll")}
                  />
                </TableHead>
                <TableHead>{t("documents.fileName")}</TableHead>
                <TableHead>{t("documents.fileType")}</TableHead>
                <TableHead>{t("documents.fileSize")}</TableHead>
                <TableHead>{t("documents.uploadDate")}</TableHead>
                <TableHead className="text-right">{t("documents.actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {documents.map((doc) => (
                <TableRow key={doc.id}>
                  <TableCell>
                    <Checkbox
                      checked={selectedDocs.includes(doc.id)}
                      onCheckedChange={() => handleToggleSelect(doc.id)}
                      aria-label={t("documents.selectFile")}
                    />
                  </TableCell>
                  <TableCell className="font-medium">{doc.file_name}</TableCell>
                  <TableCell>{doc.file_type}</TableCell>
                  <TableCell>{formatBytes(doc.file_size)}</TableCell>
                  <TableCell>{getFormattedDate(doc.created_at)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleTranslate(doc.id)}
                        title={t("translations.translateDocument")}
                      >
                        <Languages className="h-4 w-4" />
                      </Button>
                      <ProcurementDocumentAnalysis
                        document={doc}
                        tenderId={tenderId}
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onDownload(doc.file_path, doc.file_name)}
                        title={t("documents.download")}
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                      
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            className="h-8 w-8 p-0"
                            title={t("common.moreOptions")}
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => onDelete(doc.id, doc.file_path)}
                            className="text-destructive"
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            {t("documents.delete")}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {isTranslateDialogOpen && currentDocId && currentDocPath && currentFileName && (
        <DocumentTranslator
          documentId={currentDocId}
          documentPath={currentDocPath}
          fileName={currentFileName}
          tenderId={tenderId}
          isOpen={isTranslateDialogOpen}
          onClose={() => setIsTranslateDialogOpen(false)}
        />
      )}

      {isMultiAnalyzeDialogOpen && selectedDocuments.length > 0 && (
        <MultiDocumentAnalysis 
          documents={selectedDocuments}
          tenderId={tenderId}
          isOpen={isMultiAnalyzeDialogOpen}
          onClose={() => setIsMultiAnalyzeDialogOpen(false)}
        />
      )}
    </div>
  );
};
